import { useState } from 'react'
import useResource from '../useResource'
import { money, shortDate, taskCount } from '../api'
import { AgingBars, Badge, MetricCard, PageHeader, SectionTitle, StatePanel } from './Common'

const TEAM_METRICS = [
  ['total_open_debt', 'Open debt', 'account_balance_wallet', 'money', 'coral'],
  ['overdue_invoices', 'Overdue invoices', 'pending_actions', 'number', 'peach'],
  ['tasks_due_today', 'Due today', 'task_alt', 'number', 'teal'],
  ['dso', 'DSO', 'calendar_month', 'days', 'lav'],
  ['cei', 'CEI', 'speed', 'percent', 'teal'],
]

function TeamView({ businessUnit, onNavigate, refreshKey }) {
  const [collectorId, setCollectorId] = useState('')
  const resource = useResource('dashboard/team', {
    owner_scope: collectorId ? 'self' : 'all-agents',
    assignee_id: collectorId,
    business_unit: businessUnit,
  }, true, refreshKey)
  const data = resource.data
  return (
    <>
      <div className="col-toolbar"><label>Queue scope<select className="col-select" value={collectorId} onChange={(event) => setCollectorId(event.target.value)}><option value="">Team lead · all collectors</option>{Array.from({ length: 6 }, (_, index) => <option key={index} value={`collector-0${index + 1}`}>Collector {index + 1} · own queue</option>)}</select></label></div>
      <StatePanel {...resource} empty={data && !data.kpis} onRetry={resource.retry}>
        {data && <>
        <div className="col-metric-grid five">
          {TEAM_METRICS.map(([key, label, icon, format, tone]) => (
            <MetricCard key={key} label={label} icon={icon} tone={tone} format={format} value={data.kpis?.[key]} onClick={() => onNavigate('collections-work-queue')} />
          ))}
        </div>
        {data.currency_summary?.unvalued_invoice_count > 0 && <div className="col-inline-alert"><span>Open debt shows {money(data.currency_summary.valued_total_nis)} with NIS evidence. {data.currency_summary.unvalued_invoice_count} invoices have unavailable remaining balances or FX evidence and are not treated as zero.</span></div>}
        <div className="col-two-col col-overview-row">
          <section className="col-card">
            <SectionTitle title="Debt aging" text="Open exposure by days past due" />
            <AgingBars values={data.aging} />
          </section>
          <section className="col-card">
            <SectionTitle title="Priority work" text={`${taskCount({ tasks: data.tasks })} tasks surfaced for the team`} action={<button className="col-link" onClick={() => onNavigate('collections-work-queue')}>View queue →</button>} />
            <div className="col-list compact">
              {(data.tasks || []).slice(0, 6).map((task) => (
                <button className="col-list-row" key={task.id} onClick={() => onNavigate('collections-customer', { customerId: task.customer_id })}>
                  <span className="col-list-mark" />
                  <span><b>{task.title}</b><small>{task.invoice_id} · {task.assignee_id}</small></span>
                  <span><Badge tone={task.is_overdue ? 'danger' : 'warning'}>{task.status_group}</Badge><small>{shortDate(task.due_date)}</small></span>
                </button>
              ))}
              {!data.tasks?.length && <div className="col-inline-empty">No open tasks in this scope.</div>}
            </div>
          </section>
        </div>
        </>}
      </StatePanel>
    </>
  )
}

function TrendChart({ series = [] }) {
  const points = series.slice(-12)
  const max = Math.max(...points.map((point) => Number(point.total_open_debt || 0)), 1)
  return (
    <div className="col-trend-chart" role="img" aria-label="Twelve month open debt trend">
      {points.map((point) => (
        <div className="col-trend-col" key={point.as_of_date} title={`${shortDate(point.as_of_date)} — ${money(point.total_open_debt)}`}>
          <i style={{ height: `${Math.max(5, Number(point.total_open_debt || 0) / max * 100)}%` }} />
          <span>{String(point.as_of_date).slice(5, 7)}</span>
        </div>
      ))}
    </div>
  )
}

function StakeholderView({ businessUnit, onNavigate, refreshKey }) {
  const [persona, setPersona] = useState('executive')
  const [managementUnit, setManagementUnit] = useState('commit_il')
  const [dealOwner, setDealOwner] = useState('owner-01')
  const [start, setStart] = useState('2025-09-01')
  const [end, setEnd] = useState('2026-08-31')
  const [granularity, setGranularity] = useState('monthly')
  const [comparePrevious, setComparePrevious] = useState(true)
  const dimension = businessUnit ? 'business_unit' : persona === 'management' ? 'business_unit' : persona === 'account_manager' ? 'deal_owner' : 'company'
  const value = businessUnit || (persona === 'management' ? managementUnit : persona === 'account_manager' ? dealOwner : 'all')
  const snapshot = useResource('dashboard/stakeholder', { dimension, value, start, end }, true, refreshKey)
  const trends = useResource('dashboard/stakeholder/trends', { dimension, value, start, end, granularity, compare_previous: comparePrevious }, true, refreshKey)
  const data = snapshot.data
  return (
    <>
      <div className="col-toolbar cash">
        <label>Persona<select className="col-select" value={persona} disabled={Boolean(businessUnit)} onChange={(event) => setPersona(event.target.value)}><option value="executive">Executive · all companies</option><option value="management">Management · business unit</option><option value="account_manager">Account manager · own deals</option></select></label>
        {!businessUnit && persona === 'management' && <label>Business unit<select className="col-select" value={managementUnit} onChange={(event) => setManagementUnit(event.target.value)}><option value="commit_il">Commit IL</option><option value="commit_us">Commit US</option><option value="commit_eu">Commit EU</option></select></label>}
        {!businessUnit && persona === 'account_manager' && <label>Deal owner<select className="col-select" value={dealOwner} onChange={(event) => setDealOwner(event.target.value)}>{Array.from({ length: 8 }, (_, index) => <option key={index} value={`owner-0${index + 1}`}>Owner {index + 1}</option>)}</select></label>}
        <label>From<input className="col-input" type="date" value={start} onChange={(event) => setStart(event.target.value)} /></label>
        <label>To<input className="col-input" type="date" value={end} onChange={(event) => setEnd(event.target.value)} /></label>
        <label>Trend<select className="col-select" value={granularity} onChange={(event) => setGranularity(event.target.value)}><option value="monthly">Monthly</option><option value="weekly">Weekly</option><option value="daily">Daily</option></select></label>
        <label className="col-checkbox"><input type="checkbox" checked={comparePrevious} onChange={(event) => setComparePrevious(event.target.checked)} /> Compare previous period</label>
      </div>
      {businessUnit && <div className="col-config-note">The global business-unit filter is active, so stakeholder metrics are scoped to {businessUnit.replace('_', ' ').toUpperCase()}.</div>}
      <StatePanel {...snapshot} empty={data && !data.metrics} onRetry={snapshot.retry}>
        {data && <>
        <div className="col-metric-grid four">
          <MetricCard label="Open debt" value={data.metrics?.total_open_debt} icon="account_balance_wallet" format="money" onClick={() => onNavigate('collections-work-queue')} />
          <MetricCard label="Overdue" value={data.metrics?.overdue_invoices} icon="event_busy" tone="peach" onClick={() => onNavigate('collections-work-queue')} />
          <MetricCard label="DSO" value={data.metrics?.dso} icon="calendar_month" tone="lav" format="days" onClick={() => onNavigate('collections-work-queue')} />
          <MetricCard label="CEI" value={data.metrics?.cei} icon="speed" tone="teal" format="percent" onClick={() => onNavigate('collections-work-queue')} />
        </div>
        {data.currency_summary?.unvalued_invoice_count > 0 && <div className="col-inline-alert">Management NIS metrics exclude {data.currency_summary.unvalued_invoice_count} invoices needing balance reconciliation or FX evidence.</div>}
        <div className="col-two-col col-overview-row">
          <section className="col-card">
            <SectionTitle title="Receivables trend" text="Monthly open debt across the selected scope" />
            <StatePanel {...trends} empty={trends.data && !trends.data.series?.length} onRetry={trends.retry}>
              <TrendChart series={trends.data?.series} />
              {trends.data?.comparison && <div className="col-config-note">Compared with the period ending {shortDate(trends.data.comparison.previous_as_of)} · open-debt change {money(trends.data.comparison.delta?.total_open_debt || 0)}</div>}
            </StatePanel>
          </section>
          <section className="col-card">
            <SectionTitle title="Aging mix" text={`As of ${shortDate(data.as_of_date)}`} />
            <AgingBars values={data.metrics?.aging_mix} percentages />
          </section>
        </div>
        </>}
      </StatePanel>
    </>
  )
}

export default function Overview({ onNavigate, businessUnit = '', refreshKey = 0 }) {
  const [mode, setMode] = useState('team')
  return (
    <div className="col-page">
      <PageHeader title="Collections overview" description="The latest available view of debt, workload, and collection efficiency." meta={<span className="col-readonly">Collections data</span>} />
      <div className="col-tabs" role="tablist">
        <button className={mode === 'team' ? 'active' : ''} onClick={() => setMode('team')}>Team overview</button>
        <button className={mode === 'stakeholder' ? 'active' : ''} onClick={() => setMode('stakeholder')}>Stakeholder view</button>
      </div>
      {mode === 'team' ? <TeamView businessUnit={businessUnit} onNavigate={onNavigate} refreshKey={refreshKey} /> : <StakeholderView businessUnit={businessUnit} onNavigate={onNavigate} refreshKey={refreshKey} />}
    </div>
  )
}
